import { List, Map } from 'immutable'
import { BillingType, DeliveryTimeWindow } from '../domain/deliveryTimeWindow'
import { IOptimusService } from '../domain/optimusService'
import {
  bookingFormatLocalDateTime,
  getCustomerSubCustomer,
  getMeasurementVolume,
  getOptimusXYValue,
  pureVAS
} from '../pages/instant/bookingOrderFunctions'
import {
  IOptimusRouting,
  IOrderForm,
  IOrderFormFormikValues,
  ValueAddedService
} from '../pages/instant/bookingOrderTypes'
import { Department, DepartmentIdType, SlotIdType } from '../types/coreEntitiesTypes'
import { count, isEmpty } from './collectionUtils'

export type TransportLegType = 'PICKUP' | 'DELIVERY' | 'LINEHAUL' | 'TERMINAL'
export const getServiceCode = (service: IOptimusService) => {
  const serviceCode = service.get('serviceCode')
  const vasCode = service.get('vasCode') || ''
  const alystraCode = service.get('alystraServiceCode') || ''
  return `${serviceCode}___${vasCode}___${alystraCode}`
}

export const parseServiceCode = (serviceCode: string) => {
  const values = serviceCode.split('___')
  return { serviceCode: values[0], vasCode: values[1], alystraServiceCode: values[2] }
}

export const getSelectedService = (
  serviceInfo: string | undefined,
  services: List<IOptimusService>,
  customizedServices: List<IOptimusService>
) => {
  const values = serviceInfo && serviceInfo.split('___')
  if (!values) return null
  const serviceCode = values[0]
  const vasCode = values[1] || ''
  const alystraServiceCode = values[2] || ''
  const customizedService = customizedServices.find((it: IOptimusService) =>
    equalService(serviceCode, vasCode, alystraServiceCode, it)
  )
  if (isEmpty(customizedService))
    return services.find((it: IOptimusService) => equalService(serviceCode, vasCode, alystraServiceCode, it))
  else return customizedService
}

const equalService = (serviceCode: string, vasCode: string, alystraServiceCode: string, it: IOptimusService) =>
  serviceCode === it.get('serviceCode') &&
  vasCode === (it.get('vasCode') || '') &&
  alystraServiceCode === (it.get('alystraServiceCode') || '')

export function selectedService(
  services: List<IOptimusService>,
  customizedServices: List<IOptimusService>,
  serviceCode: string,
  vasCodes: List<string>
) {
  if (vasCodes.isEmpty()) {
    const customizedService = customizedServices.find(
      (it: IOptimusService) => serviceCode === it.get('serviceCode') && it.get('vasCode') === undefined
    )
    if (isEmpty(customizedService))
      return services.find(
        (it: IOptimusService) => serviceCode === it.get('serviceCode') && it.get('vasCode') === undefined
      )
    else return customizedService
  } else {
    const customizedService = customizedServices.find(
      (it: IOptimusService) => serviceCode === it.get('serviceCode') && vasCodes.contains(it.getIn(['vasCode'], ''))
    )
    if (isEmpty(customizedService))
      return services.find(
        (it: IOptimusService) => serviceCode === it.get('serviceCode') && vasCodes.contains(it.getIn(['vasCode'], ''))
      )
    else return customizedService
  }
}

export function selectedServiceForTemplateShipment(
  services: List<IOptimusService>,
  customizedServices: List<IOptimusService>,
  serviceCode: string,
  vasCodes: string[]
) {
  if (vasCodes.length === 0) {
    const customizedService = customizedServices.find(
      (it: IOptimusService) => serviceCode === it.get('serviceCode') && it.get('vasCode') === undefined
    )
    if (isEmpty(customizedService))
      return services.find(
        (it: IOptimusService) => serviceCode === it.get('serviceCode') && it.get('vasCode') === undefined
      )
    else return customizedService
  } else {
    const customizedService = customizedServices.find(
      (it: IOptimusService) => serviceCode === it.get('serviceCode') && vasCodes.includes(it.getIn(['vasCode'], ''))
    )
    if (isEmpty(customizedService))
      return services.find(
        (it: IOptimusService) => serviceCode === it.get('serviceCode') && vasCodes.includes(it.getIn(['vasCode'], ''))
      )
    else return customizedService
  }
}

export const onGetPricePayload = (
  values: IOrderForm | IOrderFormFormikValues,
  totalWeight: number,
  totalVolume: number,
  standardServiceRoutings: Map<string, IOptimusRouting> | null,
  customizedRouting: IOptimusRouting,
  services: List<IOptimusService>,
  customizedServices: List<IOptimusService>,
  allDepartments: List<Department>,
  departmentId?: DepartmentIdType,
  slotId?: SlotIdType,
  deliveryTimeWindow?: DeliveryTimeWindow,
  orderId?: number
): Map<string, any> => {
  const getValue = <T>(key: keyof IOrderFormFormikValues) =>
    (typeof (values as IOrderForm).get === 'function'
      ? (values as IOrderForm).get(key)
      : (values as IOrderFormFormikValues)[key]) as T
  const selectedServices = List(
    ((getValue('additionalServices') || []) as IOrderFormFormikValues['additionalServices']).map(
      (vas) => Map(vas) as ValueAddedService
    )
  )

  if (
    (isEmpty(services) && isEmpty(customizedServices)) ||
    !getValue('pickupDate') ||
    !getValue('pickupTimeEarliest')
  ) {
    return Map()
  }
  const service = getSelectedService(getValue('serviceId'), services, List())
  const customizedService = getSelectedService(getValue('serviceId'), List(), customizedServices)
  if (!service && !customizedService) {
    console.error('service not found', services.toJS())
    return Map()
  }

  const serviceToUse = customizedService ? customizedService : service
  const routingToUse = customizedService
    ? customizedRouting
    : serviceToUse
      ? standardServiceRoutings?.get(serviceToUse.get('alystraArticleCode'))
      : undefined
  if (!serviceToUse || !routingToUse || (serviceToUse.get('useCustomerZones') && !deliveryTimeWindow)) return Map()

  const quantity = count(getValue('measurements')) || 1
  const weight = !isNaN(totalWeight) ? totalWeight : 0
  const customerIdValues = getCustomerSubCustomer(getValue('customerId'))
  const subCustomer = customerIdValues.subcustomer || getValue('subcustomer')

  const billingType = deliveryTimeWindow ? deliveryTimeWindow.get('billingType') : undefined
  const serviceCode = getServiceCodeForPriceQuery(serviceToUse, billingType)
  const articleCode = getArticleCodeForPriceQuery(serviceToUse, serviceCode)
  const zone = getZoneForPriceQuery(serviceToUse, routingToUse, deliveryTimeWindow)

  const allValueAddedServices = services.concat(customizedServices).filter(pureVAS)
  const selectedValueAddedServices = updateValueAddedServicesWithXYAndCode(
    selectedServices.filter((vas) => vas.get('vasCode')),
    allValueAddedServices,
    zone,
    weight
  )

  const overrideDepartmentAlystraId = allDepartments
    .find((dept) => dept.get('id')?.toString() == getValue('selectedDepartmentId'))
    ?.get('alystraId')

  const selectedDepartmentAlystraId = allDepartments.find((dept) => dept.get('id') == departmentId)?.get('alystraId')

  const normalizeMeasurementVolume = (measurement: any) => {
    const volume = getMeasurementVolume(measurement)
    return Map.isMap(measurement) ? measurement.set('volume', volume) : { ...measurement, volume }
  }

  const measurements = getValue('measurements')
  const normalizedMeasurements = List.isList(measurements)
    ? measurements.map(normalizeMeasurementVolume)
    : Array.isArray(measurements)
      ? measurements.map(normalizeMeasurementVolume)
      : []

  const payload = Map({
    department: departmentId,
    customerId: customerIdValues.customerNumber,
    subcustomer: subCustomer === '' ? null : subCustomer,
    serviceId: serviceCode,
    operationsId:
      overrideDepartmentAlystraId ||
      serviceToUse.get('routingOverride') ||
      routingToUse.get('operation') ||
      selectedDepartmentAlystraId,
    articleCode,
    articleGroup: routingToUse.get('articleGroup'),
    quantity,
    weight,
    weightAddition: serviceToUse.get('weightAddition'),
    xValue: getOptimusXYValue(serviceToUse.get('xParameter'), zone, quantity, weight),
    yValue: getOptimusXYValue(serviceToUse.get('yParameter'), zone, quantity, weight),
    volume: !isNaN(totalVolume) && totalVolume > 0 ? totalVolume / 1000 : 0,
    alystraCustomer: customerIdValues.customerNumber,
    measurements: normalizedMeasurements,
    pickup: {
      zipCode: getValue('pickupZipCode'),
      streetName: getValue('pickupAddress'),
      cityName: getValue('pickupZipArea'),
      country: getValue('pickupCountryCode'),
      lat: getValue('pickupAddressLat'),
      lng: getValue('pickupAddressLng')
    },
    delivery: {
      zipCode: getValue('deliveryZipCode'),
      streetName: getValue('deliveryAddress'),
      cityName: getValue('deliveryZipArea'),
      country: getValue('deliveryCountryCode'),
      deliveryTimeEarliest: bookingFormatLocalDateTime(
        getValue('deliveryDate') || getValue('pickupDate'),
        deliveryTimeWindow?.get('timeFrom') || getValue('deliveryTimeEarliest')
      ),
      deliveryTimeLatest: bookingFormatLocalDateTime(
        getValue('deliveryDate') || getValue('pickupDate'),
        deliveryTimeWindow?.get('timeTo') || getValue('deliveryTimeLatest')
      ),
      lat: getValue('deliveryAddressLat'),
      lng: getValue('deliveryAddressLng')
    },
    from: bookingFormatLocalDateTime(getValue('pickupDate'), getValue('pickupTimeEarliest')),
    useCustomerZones: serviceToUse.get('useCustomerZones'),
    valueAddedServices: selectedValueAddedServices.toJS(),
    note: getValue('orderNote'),
    unitId: getValue('preplannedUnitId'),
    slotId,
    orderId,
    correctedArticlePrices: getValue('correctedArticlePrices'),
    ignoreBomMuteEvents: true
  })

  return payload
}

export function getServiceCodeForPriceQuery(service: IOptimusService, billingType?: BillingType): string {
  let serviceCode = service.get('alystraServiceCode')

  if (service.get('useCustomerZones') && billingType) {
    switch (billingType) {
      case BillingType.EVENING:
        serviceCode = service.get('eveningCode') || serviceCode
        break
      case BillingType.WEEKEND:
        serviceCode = service.get('weekendCode') || serviceCode
        break
      case BillingType.WEEKEND_EVENING:
        serviceCode = service.get('weekendEveningCode') || serviceCode
        break
      case BillingType.DAY:
      default:
        break
    }
  }

  return serviceCode
}

export function getArticleCodeForPriceQuery(service: IOptimusService, serviceCode: string): string {
  return service.get('useCustomerZones') ? serviceCode : service.get('alystraArticleCode')
}

export function getZoneForPriceQuery(
  service: IOptimusService,
  routing: IOptimusRouting,
  deliveryTimeWindow?: DeliveryTimeWindow
) {
  return service.get('useCustomerZones') && deliveryTimeWindow
    ? String(deliveryTimeWindow.get('zoneNumber'))
    : routing.get('zone')
}

export function updateValueAddedServicesWithXYAndCode(
  selectedVases: List<ValueAddedService>,
  allVases: List<IOptimusService>,
  zone: string,
  weight: number
): List<ValueAddedService> {
  return selectedVases.map((selectedVas) => {
    const vas = allVases.find((it: IOptimusService) => selectedVas.get('vasCode') === it.get('vasCode'))
    const quantity =
      selectedVas.get('quantity').length <= 0 || isNaN(Number(selectedVas.get('quantity')))
        ? 1
        : Number(selectedVas.get('quantity'))
    return selectedVas
      .set('xValue', getOptimusXYValue(vas ? vas.get('xParameter') : '', zone, quantity, weight))
      .set('yValue', getOptimusXYValue(vas ? vas.get('yParameter') : '', zone, quantity, weight))
      .set('vasCode', vas ? vas.get('alystraArticleCode') : '')
      .set('quantity', quantity.toString())
  })
}
